import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import CreateProjectOverlay from '../components/CreateProjectOverlay';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/Skeleton';
import { Plus, Clock, Folder, FolderKanban, Link as LinkIcon } from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      setIsScrolled(scrollElement.scrollTop > 40);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = () => {
    loadProjects();
    setShowCreateOverlay(false);
  };

  return (
    <AppShell>
      <div className="flex flex-col h-full overflow-hidden">
        <main ref={scrollRef} className="flex-1 overflow-y-auto px-10 w-full">
          {/* Sticky shrinking header */}
          <div
            className={cn(
              'sticky top-0 z-10 -mx-10 px-10 transition-all duration-300',
              isScrolled
                ? 'py-3 backdrop-blur-xl bg-white/40 dark:bg-neutral-900/40'
                : 'pt-6 pb-6 bg-transparent'
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <h1
                  className={cn(
                    'font-bold text-foreground/90 transition-all duration-300',
                    isScrolled ? 'text-lg' : 'text-3xl'
                  )}
                >
                  Projects
                </h1>
                {!isScrolled && (
                  <p className="text-muted-foreground mt-1 transition-opacity duration-300">
                    Organize your work by projects
                  </p>
                )}
              </div>

              <Button
                className="gap-2 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl"
                onClick={() => setShowCreateOverlay(true)}
              >
                <Plus className="h-4 w-4" />
                New Project
              </Button>
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2 pb-8">
              {[...Array(6)].map((_, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <Skeleton className="h-12 w-12 rounded-2xl mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-5 w-20 mb-4" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-5/6 mb-6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && projects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="rounded-2xl bg-primary/10 p-6 mb-6">
                <FolderKanban className="h-12 w-12 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground/90 mb-2">
                No projects yet
              </h3>
              <p className="text-muted-foreground mb-6">
                Create a project to get started
              </p>
              <Button
                className="gap-2 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl"
                onClick={() => setShowCreateOverlay(true)}
              >
                <Plus className="h-4 w-4" />
                New Project
              </Button>
            </div>
          )}

          {/* Projects grid */}
          {!loading && projects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2 pb-8">
              {projects.map((project, index) => (
                <Card
                  key={project.id}
                  className="hover:shadow-glass-lg transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-fade-up"
                  style={{ animationDelay: `${index * 60}ms` }}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Color icon */}
                    <div
                      className="h-12 w-12 rounded-2xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: project.color }}
                    >
                      <Folder className="h-6 w-6 text-white" />
                    </div>

                    {/* Project name and status */}
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg text-foreground/90 flex-1">
                        {project.name}
                      </h3>
                      <Badge variant="glass" className="font-medium text-xs ml-2">
                        {project.status}
                      </Badge>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3 leading-relaxed">
                      {project.description || 'No description'}
                    </p>

                    {/* Footer */}
                    <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{formatDate(project.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <LinkIcon className="h-3.5 w-3.5" />
                        <span>sources</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create project overlay */}
      {showCreateOverlay && (
        <CreateProjectOverlay
          onClose={() => setShowCreateOverlay(false)}
          onCreated={handleProjectCreated}
        />
      )}
    </AppShell>
  );
}
